import * as Sentry from "@sentry/react";
import axios from "axios";
import { ReactNode } from "react";
import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import lottieJson from "./success-lottie.json";
import {
  getErrorData,
  IDENTITY_MISMATCH_ERROR,
  livenessTexts,
} from "./utils/utils";
import { useAppDispatch } from "../../app/hooks";
import { openModal } from "../../reducers/modalReducer";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { Player } from "@lottiefiles/react-lottie-player";
import { LivenessData } from "./interfaces/types";
import { detectFaceLiveness } from "../../connectors/connectors";
import { onUpdateLivenessState } from "../../reducers/livenessReducer";
import { DocumentData, DocumentReference, setDoc } from "@firebase/firestore";
import { getCompanyPersonType } from "../../utils";
import { OnboardingType } from "../../types";
import { Alert } from "@allaria-tech/matter/Alert";
import { Icon } from "@allaria-tech/matter/Icon";
import { Typography } from "@allaria-tech/matter/Typography";
import { Feedback } from "@allaria-tech/matter/Feedback";

import styles from "./LivenessValidationCard.module.scss";

interface LivenessProps {
  livenessRef: DocumentReference<DocumentData, DocumentData>;
  sessionId: string;
  isLoadingData: boolean;
  livenessData: LivenessData;
  livenessState: string | null;
  reachedAttemptsLimit: boolean;
  error: string | null;
  handleError: (errorState?: string) => void;
  isRemoteStep: boolean;
  continueWithQR: boolean;
  isMobile: boolean;
  playerRef: React.RefObject<Player>;
  userAgentData: string;
  uuid: string;
  onboardingType: string;
  onCustomValidateFace?: (
    uuid: string,
    sessionId: string,
    livenessRef: DocumentReference<DocumentData, DocumentData>,
  ) => Promise<void>;
  onCustomValidateFaceError?: (
    error: unknown,
    reachedAttemptsLimit: boolean,
  ) => void;
  description?: ReactNode;
  testId?: string;
}

const Liveness = ({
  uuid,
  livenessRef,
  sessionId,
  isLoadingData,
  livenessData,
  livenessState,
  reachedAttemptsLimit,
  error,
  handleError,
  isRemoteStep,
  continueWithQR,
  isMobile,
  playerRef,
  userAgentData,
  onboardingType,
  onCustomValidateFace,
  onCustomValidateFaceError,
  description,
  testId = "liveness",
}: LivenessProps) => {
  const {
    livenessContainer,
    livenessMobileContainer,
    livenessExternalLinkContainer,
    centered,
    initialContainer,
    mobileInitialContainer,
    mobileInitialHeader,
    mobileInitialContent,
    validationText,
    tipsList,
    cryptoTipsList,
    loading,
    detector,
    feedbackMessage,
    contentCenter,
  } = styles;

  const dispatch = useAppDispatch();
  const { isValid } = livenessData;
  const isCrypto = onboardingType === "DIGITAL";

  const flashingLightsWarning = (
    <>
      Durante el escaneo, la pantalla emitirá luces de colores rápidas.
      {isCrypto && (
        <>
          <br />
          Si tenés sensibilidad a la luz o epilepsia fotosensible, te
          recomendamos contactar con Soporte.
        </>
      )}
    </>
  );

  const handleAnalysisComplete = async () => {
    if (sessionId) {
      try {
        if (onCustomValidateFace) {
          await onCustomValidateFace(uuid, sessionId, livenessRef);
        } else {
          const livenessResult = await detectFaceLiveness({
            companyPersonType: getCompanyPersonType(
              onboardingType as OnboardingType,
            ),
            sessionId,
          });
          const { status } = livenessResult.data;

          if (status === "OK") {
            setDoc(
              livenessRef,
              {
                isValid: true,
                userAgentData,
              },
              { merge: true },
            );
          } else {
            // status is "CHECK" or "FAILED"
            handleError();
          }
        }
      } catch (error) {
        console.log(error);
        console.log("Error in liveness validation");
        onCustomValidateFaceError?.(error, reachedAttemptsLimit);
        const isIdentityMismatch =
          axios.isAxiosError(error) &&
          error.response?.data?.code === IDENTITY_MISMATCH_ERROR;
        handleError(isIdentityMismatch ? IDENTITY_MISMATCH_ERROR : undefined);
      }
    }
  };

  const handleRequirementsClick = () => {
    dispatch(openModal({ name: "modal-minimum-requirements" }));
  };

  const { errorType, errorTitle, errorDescription, errorContent } = getErrorData(
    {
      error,
      handleRequirementsClick,
    },
  );

  return (
    <div
      className={[
        livenessContainer,
        livenessState !== "INITIAL" && centered,
        isRemoteStep && livenessExternalLinkContainer,
        isMobile && livenessMobileContainer,
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
      data-liveness-state={livenessState ?? "none"}
    >
      {isLoadingData && !livenessState && (
        <div className={loading}>
          <LoadingCircle testId={`${testId}-loader`} />
        </div>
      )}
      {livenessState === "INITIAL" &&
        (isMobile ? (
          <section
            className={mobileInitialContainer}
            data-testid={`${testId}-initial`}
          >
            <div className={mobileInitialHeader}>
              <Icon
                name="privacy"
                size={80}
                color="var(--icon-base, #3f4f69)"
              />
              <div className={mobileInitialContent}>
                <Typography
                  type="HEADING"
                  size="M"
                  color="var(--typography-heading-color-base, #3f4f69)"
                >
                  Vamos a validar tu identidad
                </Typography>
                <Typography
                  type="BODY"
                  size="S"
                  color="var(--typography-body-color-base, #3f4f69)"
                >
                  Acercate a un lugar con buena iluminación, sacate cualquier
                  accesorio facial y subí el brillo de tu dispositivo.
                </Typography>
              </div>
            </div>
            <Alert variant="WARN" testId={`${testId}-initial-alert`}>
              {flashingLightsWarning}
            </Alert>
          </section>
        ) : (
          <section
            className={initialContainer}
            data-testid={`${testId}-initial`}
          >
            {description}
            <div className={validationText}>
              <ul className={`${tipsList} ${isCrypto ? cryptoTipsList : ""}`}>
                <li>
                  <Icon
                    name="sunset"
                    size="md"
                    color="var(--icon-emphasis, #2b58a1)"
                  />
                  <span>Acercate a un lugar con buena iluminación.</span>
                </li>
                <li>
                  <Icon
                    name="ambient-mode"
                    size="md"
                    color="var(--icon-emphasis, #2b58a1)"
                  />
                  <span>Sacate cualquier accesorio facial.</span>
                </li>
                <li>
                  <Icon
                    name="bright"
                    size="md"
                    color="var(--icon-emphasis, #2b58a1)"
                  />
                  <span>Subí el brillo de tu dispositivo.</span>
                </li>
              </ul>
            </div>
            <Alert
              variant="WARN"
              className="my-3"
              testId={`${testId}-initial-alert`}
            >
              {flashingLightsWarning}
            </Alert>
          </section>
        ))}
      {sessionId && livenessState === "DETECTOR" && (
        <div className={detector} data-testid={`${testId}-detector`}>
          <FaceLivenessDetector
            sessionId={sessionId}
            region="us-east-1"
            onAnalysisComplete={handleAnalysisComplete}
            disableStartScreen
            onError={(error) => {
              Sentry.captureException(error);
              handleError(error.state);
            }}
            displayText={livenessTexts}
          />
        </div>
      )}
      {livenessState === "CAMERA_ACCESS_REQUIRED" && (
        <Feedback
          inline
          type="WARNING"
          title="Conectá una cámara para validar tu identidad"
        />
      )}
      {isValid && !livenessState && (
        <div className={`${contentCenter}`} data-testid={`${testId}-success`}>
          <Player
            onEvent={(event) => {
              if (event === "complete" || event === "error")
                dispatch(onUpdateLivenessState("VALIDATED"));
            }}
            ref={playerRef}
            src={lottieJson}
            loop={false}
            style={{
              width: "75%",
              padding: "3rem",
            }}
          />
        </div>
      )}
      {livenessState === "VALIDATED" && (
        <Feedback
          inline
          type="SUCCESS"
          title={
            continueWithQR
              ? "¡Listo! Podés cerrar esta página y seguir en tu computadora."
              : "¡Validaste tu identidad!"
          }
          description={
            continueWithQR
              ? undefined
              : isRemoteStep
                ? "Podés cerrar esta página"
                : "Podés continuar"
          }
        />
      )}
      {livenessState === "NOT_VALIDATED" && (
        <Feedback
          inline
          type={errorType}
          title={errorTitle}
          description={errorDescription}
        >
          {errorContent && (
            <div className={feedbackMessage} data-testid={`${testId}-error`}>
              {errorContent}
            </div>
          )}
        </Feedback>
      )}
    </div>
  );
};

export default Liveness;
